import React from 'react';

import { titleize } from '@admin/components/helpers/titleize';
import { OrderSubtypeEnum, OrderTypeEnum } from '@admin/schema';

// Type for Angular data (string values)
interface AngularOrderData {
  moving?: boolean;
  storage?: boolean;
  type?: string;
  subtype?: string;
  maintenance?: boolean;
}

// Type for React/GraphQL data (enum values)
interface ReactOrderData {
  moving?: boolean;
  storage?: boolean;
  type?: OrderTypeEnum;
  subtype?: OrderSubtypeEnum;
  maintenance?: boolean;
}

// Union type that can handle both formats
type OrderData = AngularOrderData | ReactOrderData;

const orderKindDescription = (order: OrderData, legacyFormat = false) => {
  let description = '';

  // Helper function to append text with proper spacing
  const appendWithSpace = (text: string) => {
    if (description && text) {
      description += ' ' + text;
    } else if (text) {
      description += text;
    }
  };

  if (order.moving) appendWithSpace('Moving');
  if (order.moving && order.storage) appendWithSpace('& Pickup');

  if (legacyFormat) {
    // Original Angular template logic
    if (!order.moving && order.storage && order.type !== 'selfstorage-shuttle' && order.type !== 'retail-delivery')
      appendWithSpace(titleize(order.type || ''));
    if (order.type === 'selfstorage-shuttle') appendWithSpace('Self-Storage Shuttle');
    if (order.type === 'retail-delivery') appendWithSpace('Delivery');
    if (order.type === 'taskorder') appendWithSpace('Task Order');
    if (order.type === 'disposal') appendWithSpace('Disposal');

    if (order.subtype) {
      appendWithSpace(`- ${titleize(order.subtype)}`);
    }
  } else {
    // Modern GraphQL enum-based logic
    if (
      !order.moving &&
      order.storage &&
      order.type !== OrderTypeEnum.SelfStorageShuttle &&
      order.type !== OrderTypeEnum.RetailDelivery &&
      order.type !== OrderTypeEnum.Disposal
    )
      appendWithSpace(titleize(order.type || ''));
    if (order.type === OrderTypeEnum.TaskOrder) appendWithSpace('Task Order');
    if (order.type === OrderTypeEnum.Disposal) appendWithSpace('Disposal');
    if (order.type === OrderTypeEnum.SelfStorageShuttle) appendWithSpace('Self-Storage Shuttle');
    if (order.type === OrderTypeEnum.RetailDelivery) appendWithSpace('Delivery');
    if (order.subtype !== OrderSubtypeEnum.None && order.subtype) {
      appendWithSpace(`- ${titleize(order.subtype.toLocaleLowerCase())}`);
    }
  }

  if (order.maintenance) appendWithSpace('- Maintenance');

  return description;
};

interface OrderKindDescriptionProps {
  order: OrderData;
  legacyFormat?: boolean; // Use original Angular template format
}

export const OrderKindDescription: React.FC<OrderKindDescriptionProps> = ({ order, legacyFormat = false }) => {
  return <span>{orderKindDescription(order, legacyFormat)}</span>;
};
